import React, { useMemo, useEffect, useRef } from 'react';
import { Bell } from 'lucide-react';
import { useMobileNavigate } from '../mobile/MobileNavigationGuard';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Button } from '../ui/button';
import { useNotificationsContext } from '../../context/NotificationsContext';
import { NotificationItem } from './NotificationItem';
import { useApiBase } from '../../hooks/useApiBase';
import { markAllAsRead } from '../../api/notifications';
import { useModalState } from '../../hooks/useModalState';
import { useModalScrollFix } from '../../hooks/useModalScrollFix';
import type { Notification } from '../../types/notifications';

interface NotificationPanelProps {
  children: React.ReactNode;
}

// Extract notification filtering logic (DRY/SOLID)
function useNotificationGroups(notifications: Notification[]) {
  return useMemo(() => {
    const unread = notifications.filter(n => !n.read);
    const read = notifications.filter(n => n.read);
    return { unread, read };
  }, [notifications]);
}

export function NotificationPanel({ children }: NotificationPanelProps) {
  const apiBase = useApiBase();
  const navigate = useMobileNavigate();
  const { notifications, loading, hasUnread, refresh, refreshUnreadCount, markAsRead, deleteNotification, requestFullList, releaseFullList } = useNotificationsContext();
  const { isOpen, setOpen } = useModalState();
  const { unread: unreadNotifications, read: readNotifications } = useNotificationGroups(notifications);
  const prevIsOpenRef = useRef(isOpen);
  const refreshRef = useRef(refresh);
  const scrollRef = useRef<HTMLDivElement>(null);
  useModalScrollFix(scrollRef);

  // Keep refresh ref up to date
  useEffect(() => {
    refreshRef.current = refresh;
  }, [refresh]);

  // Request full list when panel opens, release when closed
  useEffect(() => {
    if (isOpen) {
      requestFullList(); // Request full list when panel opens
      // Only refresh when transitioning from closed to open
      if (!prevIsOpenRef.current) {
        refreshRef.current(); // Refresh when panel opens
      }
      prevIsOpenRef.current = isOpen;
      return () => {
        releaseFullList(); // Release when panel closes or component unmounts
      };
    } else {
      prevIsOpenRef.current = isOpen;
    }
  }, [isOpen, requestFullList, releaseFullList]);

  return (
    <Popover open={isOpen} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {children}
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0" align="end">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="font-semibold text-sm">Notifications</h3>
          {hasUnread && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={async () => {
                try {
                  await markAllAsRead(apiBase);
                  refresh();
                  refreshUnreadCount(); // Refresh unread count after bulk update
                } catch (err) {
                  // Error handled by hook
                }
              }}
            >
              Mark all read
            </Button>
          )}
        </div>
        <div ref={scrollRef} className="overflow-y-auto max-h-[500px]">
          {loading ? (
            <div className="p-4 text-center text-sm text-muted-foreground">
              Loading notifications...
            </div>
          ) : notifications.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">
              <Bell className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>No notifications</p>
            </div>
          ) : (
            <div className="divide-y">
              {unreadNotifications.length > 0 && (
                <div>
                  <div className="px-4 py-2 text-xs font-medium text-muted-foreground bg-muted/50">
                    Unread
                  </div>
                  {unreadNotifications.map(notification => (
                    <NotificationItem
                      key={notification.id}
                      notification={notification}
                      onMarkAsRead={(read) => markAsRead(notification.id, read)}
                      onDelete={() => deleteNotification(notification.id)}
                    />
                  ))}
                </div>
              )}
              {readNotifications.length > 0 && (
                <div>
                  <div className="px-4 py-2 text-xs font-medium text-muted-foreground bg-muted/50">
                    Read
                  </div>
                  {readNotifications.map(notification => (
                    <NotificationItem
                      key={notification.id}
                      notification={notification}
                      onMarkAsRead={(read) => markAsRead(notification.id, read)}
                      onDelete={() => deleteNotification(notification.id)}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        <div className="border-t p-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="w-full justify-center"
            onClick={() => {
              setOpen(false);
              navigate('/notifications');
            }}
          >
            View all notifications
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
